/**
 * EPIC map output: the geometry decisions behind the PNG.
 *
 * EPIC output is an image, not an AutoCAD package - walls only, on white, cropped to the
 * conversion polygon. The desktop application rendered it in `EpicMapExporter` with WPF, and
 * this is that logic recovered from commit `738b9b1^` and generalized to the geometry the
 * current engine actually produces. The rules it preserves, because they are what makes the
 * output recognisable to whoever consumes it:
 *
 *   - white ground, black linework, nothing else. No doors, no text, no room tags.
 *   - cropped to the conversion polygon's bounding box, and clipped to the polygon itself.
 *   - exterior walls stroke at 3 points, interior at 2, rendered at 96 DPI.
 *
 * **The one thing that could not be ported verbatim is which field holds the walls.** The
 * old exporter drew `WallLines`, and the vector-PDF pivot left that list empty on the raster
 * path - `RecognitionEngine` returns `WallLines = new List<DetectedLine>()` and puts the
 * walls in `Polylines`. And EPIC is *always* the raster path: the vector branch is gated on
 * `isPdf && outputType == OutputType.DwgFloorPlan`, so an EPIC job never reaches it. A
 * verbatim port would therefore have rendered a blank white image on every real job. This
 * reads the same carriers the AutoCAD connector counts when it validates a package -
 * polylines and splines as well as legacy wall lines - so it draws whichever the engine
 * filled in.
 *
 * Everything here is pure and unit-tested. Only the canvas call that turns a plan into
 * pixels lives in the browser, so a server-side renderer could reuse this untouched.
 */

import type { Point, Size } from "./preview-coordinates.ts";

/** WPF device-independent pixels per typographic point. The old exporter's `96.0 / 72.0`. */
export const POINTS_TO_PIXELS = 96 / 72;

/** Stroke width for a wall on the outer boundary of the plan, in points. */
export const EXTERIOR_WALL_POINTS = 3.0;

/** Stroke width for every other wall, in points. */
export const INTERIOR_WALL_POINTS = 2.0;

export interface Rect {
  readonly x: number;
  readonly y: number;
  readonly width: number;
  readonly height: number;
}

/**
 * The geometry carriers an EPIC render reads, as a structural subset of the engine's
 * `RecognitionResult`. Declared here rather than imported so this package does not depend on
 * the API client's types; the field names are the engine's own.
 */
export interface EpicGeometry {
  /** Legacy carrier. Empty on the raster path, populated by the vector path. */
  readonly wallLines?: readonly { readonly start: Point; readonly end: Point }[];
  /** Where the raster path puts walls, and therefore where every EPIC job puts them. */
  readonly polylines?: readonly { readonly vertices: readonly Point[]; readonly closed?: boolean }[];
  /** Architectural curves kept as curves. Flattened through their fit points. */
  readonly splines?: readonly { readonly fitPoints: readonly Point[] }[];
}

export interface EpicSegment {
  readonly start: Point;
  readonly end: Point;
  /** Stroke width in points: {@link EXTERIOR_WALL_POINTS} or {@link INTERIOR_WALL_POINTS}. */
  readonly widthPoints: number;
}

export interface EpicMapPlan {
  /** The crop in preview space. A renderer translates by its negated origin. */
  readonly crop: Rect;
  /** Pixel size of the PNG. Ceiling of the crop, never below 1. */
  readonly size: Size;
  /** Only the segments that fall inside the crop, with their stroke widths decided. */
  readonly segments: readonly EpicSegment[];
  /** The clip path, in preview space. Null when the polygon is degenerate. */
  readonly clipPolygon: readonly Point[] | null;
}

/**
 * The crop: the polygon's bounding box, or the whole preview when there is no usable polygon.
 *
 * A conversion polygon is mandatory in the web application, so the fallback is defensive
 * rather than a supported path - but the engine tolerates a degenerate selection and returns
 * geometry for the whole page, and cropping that to nothing would be worse than showing it.
 */
export function epicCropBounds(polygon: readonly Point[], preview: Size): Rect {
  if (polygon.length < 3) {
    return { x: 0, y: 0, width: Math.max(1, preview.width), height: Math.max(1, preview.height) };
  }
  const xs = polygon.map((p) => p.x);
  const ys = polygon.map((p) => p.y);
  const minX = Math.min(...xs);
  const minY = Math.min(...ys);
  return {
    x: minX,
    y: minY,
    width: Math.max(1, Math.max(...xs) - minX),
    height: Math.max(1, Math.max(...ys) - minY),
  };
}

/**
 * Every wall as a straight segment, from whichever carrier the engine filled in.
 *
 * A closed chain gets its closing segment, because a chain that draws as an open loop reads
 * as a missing wall rather than as a rendering choice.
 */
export function epicWallSegments(geometry: EpicGeometry): { readonly start: Point; readonly end: Point }[] {
  const segments: { start: Point; end: Point }[] = [];

  for (const line of geometry.wallLines ?? []) {
    segments.push({ start: line.start, end: line.end });
  }

  for (const chain of geometry.polylines ?? []) {
    pushChain(segments, chain.vertices, chain.closed === true);
  }

  for (const spline of geometry.splines ?? []) {
    pushChain(segments, spline.fitPoints, false);
  }

  return segments;
}

function pushChain(
  into: { start: Point; end: Point }[],
  vertices: readonly Point[],
  closed: boolean,
): void {
  for (let i = 1; i < vertices.length; i += 1) {
    into.push({ start: vertices[i - 1]!, end: vertices[i]! });
  }
  if (closed && vertices.length > 2) {
    into.push({ start: vertices[vertices.length - 1]!, end: vertices[0]! });
  }
}

function intersectsCrop(segment: { start: Point; end: Point }, crop: Rect): boolean {
  const minX = Math.min(segment.start.x, segment.end.x);
  const maxX = Math.max(segment.start.x, segment.end.x);
  const minY = Math.min(segment.start.y, segment.end.y);
  const maxY = Math.max(segment.start.y, segment.end.y);
  return (
    maxX >= crop.x && minX <= crop.x + crop.width && maxY >= crop.y && minY <= crop.y + crop.height
  );
}

interface Bounds {
  readonly minX: number;
  readonly minY: number;
  readonly maxX: number;
  readonly maxY: number;
}

function boundsOf(segments: readonly { start: Point; end: Point }[]): Bounds {
  if (segments.length === 0) return { minX: 0, minY: 0, maxX: 0, maxY: 0 };
  let minX = Infinity;
  let minY = Infinity;
  let maxX = -Infinity;
  let maxY = -Infinity;
  for (const s of segments) {
    minX = Math.min(minX, s.start.x, s.end.x);
    minY = Math.min(minY, s.start.y, s.end.y);
    maxX = Math.max(maxX, s.start.x, s.end.x);
    maxY = Math.max(maxY, s.start.y, s.end.y);
  }
  return { minX, minY, maxX, maxY };
}

/**
 * Whether a wall lies on the outer boundary, and so draws heavier.
 *
 * The old exporter's rule, unchanged: both endpoints within a tolerance of the same edge of
 * the drawing's bounding box. The tolerance scales with the plan - 1.2% of its longest side,
 * floored at 5 preview units - because a fixed tolerance that works on a classroom wing is
 * meaningless on a full campus sheet.
 *
 * It is a heuristic and it was one in the desktop application too: an interior wall that
 * happens to run flush with the building line draws heavy. That is a visual weight on an
 * image nothing measures, so it is not worth more than this.
 */
export function isExteriorWall(segment: { start: Point; end: Point }, bounds: Bounds): boolean {
  const tolerance = Math.max(5.0, Math.max(bounds.maxX - bounds.minX, bounds.maxY - bounds.minY) * 0.012);
  const onEdge = (a: number, b: number, edge: number): boolean =>
    Math.abs(a - edge) <= tolerance && Math.abs(b - edge) <= tolerance;
  return (
    onEdge(segment.start.x, segment.end.x, bounds.minX) ||
    onEdge(segment.start.x, segment.end.x, bounds.maxX) ||
    onEdge(segment.start.y, segment.end.y, bounds.minY) ||
    onEdge(segment.start.y, segment.end.y, bounds.maxY)
  );
}

/**
 * Everything a renderer needs, decided.
 *
 * The order matters and is the old exporter's: crop, then discard what falls outside it, then
 * take the bounds of *what is left* to classify exteriors against. Classifying against the
 * whole drawing instead would call a wall exterior because of a wing that was cropped away.
 */
export function planEpicMap(
  geometry: EpicGeometry,
  polygon: readonly Point[],
  preview: Size,
): EpicMapPlan {
  const crop = epicCropBounds(polygon, preview);
  const visible = epicWallSegments(geometry).filter((s) => intersectsCrop(s, crop));
  const bounds = boundsOf(visible);

  return {
    crop,
    size: {
      width: Math.max(1, Math.ceil(crop.width)),
      height: Math.max(1, Math.ceil(crop.height)),
    },
    segments: visible.map((s) => ({
      start: s.start,
      end: s.end,
      widthPoints: isExteriorWall(s, bounds) ? EXTERIOR_WALL_POINTS : INTERIOR_WALL_POINTS,
    })),
    clipPolygon: polygon.length >= 3 ? polygon : null,
  };
}

/**
 * The file name a drafter gets. The X1 package names itself from the school, so this does
 * too, and for the same reason: a downloads folder of `package.png` is unusable.
 */
export function epicMapFileName(schoolName: string): string {
  const trimmed = schoolName.trim();
  return trimmed === "" ? "EPIC Map.png" : `EPIC - ${trimmed}.png`;
}
